import {
  Avatar,
  Badge,
  Button,
  Inline,
  PageHeader,
  Panel,
  Skeleton,
  TabNav,
  TabNavItem,
  Text,
  TextLink,
} from "@nudgebox/ui";
import { SendIcon } from "@nudgebox/ui/icons";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, Outlet, useMatchRoute, useParams } from "@tanstack/react-router";
import { PendingOutlet, TabPending } from "@/app/route-pending.tsx";
import { useAppParams } from "@/lib/params.ts";
import { applicationQuery, applicationsQuery } from "@/lib/queries.ts";

const TABS = [
  { to: "/$orgSlug/apps/$appSlug", label: "Overview", fuzzy: false },
  { to: "/$orgSlug/apps/$appSlug/notifications", label: "Notifications", fuzzy: true },
  { to: "/$orgSlug/apps/$appSlug/subscribers", label: "Subscribers", fuzzy: false },
  { to: "/$orgSlug/apps/$appSlug/channels", label: "Channels", fuzzy: false },
  { to: "/$orgSlug/apps/$appSlug/settings", label: "Settings", fuzzy: false },
] as const;

export function ApplicationLayout() {
  const { org, app } = useAppParams();
  const matchRoute = useMatchRoute();
  const { data } = useSuspenseQuery(applicationQuery(org, app));
  const { application } = data;
  const params = { orgSlug: org, appSlug: app };
  const composing = Boolean(matchRoute({ to: "/$orgSlug/apps/$appSlug/notifications/new", params }));
  const suspended = application.suspendedAt !== null;

  return (
    <>
      <PageHeader
        leading={<Avatar name={application.name} src={application.iconUrl} size={40} square />}
        title={application.name}
        description={
          <Inline gap="sm">
            <Text size="sm" tone="tertiary" mono>
              {application.slug}
            </Text>
            {suspended ? <Badge tone="error">suspended</Badge> : null}
            {application.websiteUrl ? (
              <>
                <Text size="sm" tone="tertiary">
                  ·
                </Text>
                <TextLink href={application.websiteUrl} target="_blank" rel="noreferrer">
                  {new URL(application.websiteUrl).host}
                </TextLink>
              </>
            ) : null}
          </Inline>
        }
        actions={
          composing || suspended ? null : (
            <Button
              variant="primary"
              icon={<SendIcon size={14} />}
              render={<Link to="/$orgSlug/apps/$appSlug/notifications/new" params={params} />}
              nativeButton={false}
            >
              Send notification
            </Button>
          )
        }
      />
      {suspended ? (
        <Panel tone="danger" title="This application is suspended">
          <Text size="sm">
            After reviewing reports from followers, Nudgebox suspended {application.name}. It can't send notifications
            or gain followers, and its follow page is offline. If you think this is a mistake, write to{" "}
            <TextLink href="mailto:hello@nudgebox.app">hello@nudgebox.app</TextLink>.
          </Text>
        </Panel>
      ) : null}
      <ApplicationTabs orgSlug={org} appSlug={app} />
      <Outlet />
    </>
  );
}

/**
 * While the application loads: the header from the organization's app list
 * (already loaded by the shell) when it has this app, real tabs, and the
 * opening tab's skeleton.
 */
export function ApplicationPending() {
  const { orgSlug = "", appSlug = "" } = useParams({ strict: false });
  const { data: applications } = useQuery({ ...applicationsQuery(orgSlug), enabled: Boolean(orgSlug) });
  const application = applications?.find((candidate) => candidate.slug === appSlug);

  return (
    <div aria-busy>
      <PageHeader
        leading={
          application ? (
            <Avatar name={application.name} src={application.iconUrl} size={40} square />
          ) : (
            <Skeleton width={40} height={40} radius="md" />
          )
        }
        title={application?.name ?? <Skeleton width={180} height={24} />}
        description={
          <Text size="sm" tone="tertiary" mono>
            {appSlug}
          </Text>
        }
        actions={<Skeleton width={164} height={34} radius="md" />}
      />
      <ApplicationTabs orgSlug={orgSlug} appSlug={appSlug} />
      <PendingOutlet fallback={<TabPending />} />
    </div>
  );
}

function ApplicationTabs({ orgSlug, appSlug }: { orgSlug: string; appSlug: string }) {
  const matchRoute = useMatchRoute();
  const params = { orgSlug, appSlug };
  return (
    <TabNav aria-label="Application">
      {TABS.map((tab) => (
        <TabNavItem
          key={tab.to}
          render={<Link to={tab.to} params={params} search={{}} />}
          active={Boolean(matchRoute({ to: tab.to, params, fuzzy: tab.fuzzy }))}
        >
          {tab.label}
        </TabNavItem>
      ))}
    </TabNav>
  );
}
